import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/context/AuthContext'
import * as storage from '@/lib/safeStorage'
import type { MembershipWithOrg, Organization } from '@/types'

interface OrgContextValue {
  memberships: MembershipWithOrg[]
  activeOrg: Organization | null
  activeMembership: MembershipWithOrg | null
  setActiveOrgId: (orgId: string) => void
  isPlatformAdmin: boolean
  loading: boolean
  // Separate flag for the platform-admin check so routes that only care
  // about admin status (the 0-org branch) can wait for it, while the
  // main app doesn't block on it.
  adminLoading: boolean
}

const OrgContext = createContext<OrgContextValue | null>(null)
const STORAGE_KEY = 'tourflow-selected-org'

export function OrgProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth()

  // Load this user's memberships joined with their orgs.
  const membershipsQuery = useQuery({
    queryKey: ['memberships', user?.id],
    enabled: !!user,
    queryFn: async (): Promise<MembershipWithOrg[]> => {
      const { data, error } = await supabase
        .from('organization_memberships')
        .select('*, organization:organizations(*)')
        .eq('user_id', user!.id)

      if (error) throw error
      return (data ?? []).filter(m => m.organization) as MembershipWithOrg[]
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  })

  const adminQuery = useQuery({
    queryKey: ['platform-admin', user?.id],
    enabled: !!user,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase
        .from('platform_admins')
        .select('user_id')
        .eq('user_id', user!.id)
        .maybeSingle()

      if (error) throw error
      return !!data
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  })

  const memberships = membershipsQuery.data ?? []

  const [activeOrgIdState, setActiveOrgIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    return storage.getItem(STORAGE_KEY)
  })

  // When the user signs out, drop the in-memory selection so the next
  // login starts at the picker instead of reusing the previous org.
  // Track previous user to avoid clearing on initial mount (user starts
  // as null while auth loads — clearing then would wipe the saved org).
  const prevUser = useRef(user)
  useEffect(() => {
    if (prevUser.current && !user) {
      setActiveOrgIdState(null)
    }
    prevUser.current = user
  }, [user])

  // Resolve the active membership from state + memberships. When the saved
  // selection is invalid (e.g., user was removed from that org), we fall back
  // to the first membership only if there's exactly one — otherwise we leave
  // it null so the app routes to the picker.
  const activeMembership = useMemo<MembershipWithOrg | null>(() => {
    if (memberships.length === 0) return null
    if (activeOrgIdState) {
      const hit = memberships.find(m => m.org_id === activeOrgIdState)
      if (hit) return hit
    }
    if (memberships.length === 1) return memberships[0]
    return null
  }, [memberships, activeOrgIdState])

  // Keep localStorage in sync when the resolved active org comes from the
  // "only one membership" fallback.
  useEffect(() => {
    if (activeMembership && activeMembership.org_id !== activeOrgIdState) {
      setActiveOrgIdState(activeMembership.org_id)
      storage.setItem(STORAGE_KEY, activeMembership.org_id)
    }
  }, [activeMembership, activeOrgIdState])

  function setActiveOrgId(orgId: string) {
    setActiveOrgIdState(orgId)
    storage.setItem(STORAGE_KEY, orgId)
  }

  const value: OrgContextValue = {
    memberships,
    activeOrg: activeMembership?.organization ?? null,
    activeMembership,
    setActiveOrgId,
    isPlatformAdmin: adminQuery.data ?? false,
    loading: authLoading || membershipsQuery.isLoading,
    adminLoading: adminQuery.isLoading,
  }

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>
}

export function useOrg() {
  const ctx = useContext(OrgContext)
  if (!ctx) throw new Error('useOrg must be used inside OrgProvider')
  return ctx
}
